// Disposition codes: a table edited row by row (PATCH changed fields), plus a form for a new code (POST).

import { useState } from 'react';
import { api, type DispositionCode } from '../../api.ts';
import { Icon } from '../../components/Icon.tsx';
import { Card, Empty, ErrorBox, InlineError, Loading } from '../../components/ui.tsx';
import { human } from '../../console/util.ts';
import { useLoad } from '../../hooks.ts';

const FLAGS = [
  ['billable', 'Billable'],
  ['notifiesCustomer', 'Customer report'],
  ['requiresPoliceNumber', 'Needs police #'],
  ['requiresNotes', 'Needs notes'],
  ['requiresRequiredSteps', 'Needs required steps'],
  ['active', 'Active'],
] as const;
type Flag = (typeof FLAGS)[number][0];

const BLANK: DispositionCode = {
  code: '',
  label: '',
  category: '',
  billable: false,
  notifiesCustomer: false,
  requiresPoliceNumber: false,
  requiresNotes: false,
  requiresRequiredSteps: true,
  active: true,
  sortOrder: 100,
};

function diff(a: DispositionCode, b: DispositionCode): Partial<DispositionCode> {
  const out: Partial<DispositionCode> = {};
  (Object.keys(b) as (keyof DispositionCode)[]).forEach((k) => {
    if (a[k] !== b[k]) Object.assign(out, { [k]: b[k] });
  });
  return out;
}

function RowEditor({ value, isNew, categories, onSave, onCancel, busy, error }: { value: DispositionCode; isNew: boolean; categories: string[]; onSave: (d: DispositionCode) => void; onCancel: () => void; busy: boolean; error: unknown }) {
  const [d, setD] = useState(value);
  const bad = !d.code.trim() || !d.label.trim() || !d.category.trim() || !/^[A-Z0-9_-]+$/i.test(d.code.trim());
  return (
    <tr className="subrow">
      <td colSpan={FLAGS.length + 5}>
        <div className="editgrid">
          <label className="field">
            <span className="lbl">Code</span>
            <input value={d.code} onChange={(e) => setD({ ...d, code: e.target.value.toUpperCase() })} disabled={!isNew} className="mono" placeholder="FA-ANIMAL" autoFocus={isNew} />
          </label>
          <label className="field">
            <span className="lbl">Label</span>
            <input value={d.label} onChange={(e) => setD({ ...d, label: e.target.value })} autoFocus={!isNew} />
          </label>
          <label className="field">
            <span className="lbl">Category</span>
            <input value={d.category} onChange={(e) => setD({ ...d, category: e.target.value })} list="dispo-cats" className="mono" placeholder="false_alarm" />
            <datalist id="dispo-cats">
              {categories.map((c) => (
                <option key={c} value={c} />
              ))}
            </datalist>
          </label>
          <label className="field">
            <span className="lbl">Sort order</span>
            <input type="number" value={d.sortOrder} onChange={(e) => setD({ ...d, sortOrder: Number(e.target.value) || 0 })} />
          </label>
        </div>
        <div className="row-wrap mt">
          {FLAGS.map(([k, l]) => (
            <label key={k} className="ck-inline">
              <input type="checkbox" checked={d[k]} onChange={(e) => setD({ ...d, [k]: e.target.checked })} />
              {l}
            </label>
          ))}
        </div>
        <div className="savebar">
          <button type="button" className="btn pri" disabled={bad || busy} onClick={() => onSave({ ...d, code: d.code.trim(), label: d.label.trim(), category: d.category.trim() })}>
            <Icon name="check" />
            {isNew ? 'Add code' : 'Save'}
          </button>
          <button type="button" className="btn" onClick={onCancel}>
            Cancel
          </button>
          {bad && <span className="note">Code (letters, digits, - or _), label and category are required.</span>}
          <InlineError error={error} />
        </div>
      </td>
    </tr>
  );
}

export function DispositionsEditor({ canEdit }: { canEdit: boolean }) {
  const load = useLoad((signal) => api.setupDispositions(signal), []);
  const [editing, setEditing] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const rows = (load.data ?? []).slice().sort((a, b) => a.category.localeCompare(b.category) || a.sortOrder - b.sortOrder);
  const categories = [...new Set(rows.map((r) => r.category))];

  const save = async (orig: DispositionCode | null, d: DispositionCode) => {
    setBusy(true);
    setErr(null);
    try {
      const saved = orig ? await api.patchDisposition(orig.code, diff(orig, d)) : await api.createDisposition(d);
      load.setData((l) => (orig ? (l ?? []).map((x) => (x.code === orig.code ? saved : x)) : [...(l ?? []), saved]));
      setEditing(null);
    } catch (e) {
      setErr(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card
      title={
        <>
          <Icon name="tag" />
          Disposition codes
        </>
      }
      right={
        canEdit && (
          <button type="button" className="btn" onClick={() => (setEditing('__new'), setErr(null))} disabled={editing === '__new'}>
            <Icon name="plus" />
            New code
          </button>
        )
      }
    >
      <div className="note">Operators close events with these. Inactive codes stay on closed events but are not offered. Codes cannot be renamed.</div>
      {load.error && !load.data ? (
        <ErrorBox error={load.error} onRetry={load.reload} title="Could not load disposition codes" />
      ) : !load.data ? (
        <Loading label="Loading codes…" />
      ) : (
        <div className="tw">
          <table>
            <thead>
              <tr>
                <th scope="col">Code</th>
                <th scope="col">Label</th>
                <th scope="col">Category</th>
                <th scope="col" className="num">Sort</th>
                {FLAGS.map(([k, l]) => (
                  <th key={k} scope="col" className="center">
                    {l}
                  </th>
                ))}
                <th scope="col" />
              </tr>
            </thead>
            <tbody>
              {editing === '__new' && <RowEditor value={BLANK} isNew categories={categories} onSave={(d) => void save(null, d)} onCancel={() => setEditing(null)} busy={busy} error={err} />}
              {rows.length === 0 && editing !== '__new' && (
                <tr>
                  <td colSpan={FLAGS.length + 5}>
                    <Empty>No codes yet.</Empty>
                  </td>
                </tr>
              )}
              {rows.map((r) =>
                editing === r.code ? (
                  <RowEditor key={r.code} value={r} isNew={false} categories={categories} onSave={(d) => void save(r, d)} onCancel={() => setEditing(null)} busy={busy} error={err} />
                ) : (
                  <tr key={r.code} className={r.active ? '' : 'inactive'}>
                    <td className="mono">{r.code}</td>
                    <td>{r.label}</td>
                    <td>{human(r.category)}</td>
                    <td className="mono num">{r.sortOrder}</td>
                    {FLAGS.map(([k]: readonly [Flag, string]) => (
                      <td key={k} className="center">
                        {r[k] ? <Icon name="check" className="ok-t" title="Yes" /> : <span className="dim">—</span>}
                      </td>
                    ))}
                    <td>
                      {canEdit && (
                        <button type="button" className="btn sm" onClick={() => (setEditing(r.code), setErr(null))}>
                          Edit
                        </button>
                      )}
                    </td>
                  </tr>
                ),
              )}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
